import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import IconArrowDownUp from "../../../assets/icon_arrow_down_up.svg";
import { SORT_OPTIONS, getSortOptionsArray } from "./SortOptions";
import "./SortDropdown.css";

/**
 * 검색 결과 정렬 드롭다운 컴포넌트
 * 선택된 정렬 옵션을 표시하고 변경할 수 있는 드롭다운 UI
 */
const SortDropdown = ({ selectedSort, onSortChange, origin, sortOptions: customSortOptions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const originAllowedSortCodes = useMemo(() => {
    switch (origin) {
      case "SELLER":
        return [
          SORT_OPTIONS.NEW.code,
          SORT_OPTIONS.LIKE.code,
          SORT_OPTIONS.DISCOUNT.code,
          SORT_OPTIONS.LOW_PRICE.code,
          SORT_OPTIONS.HIGH_PRICE.code,
        ];
      case "BRAND":
        return [
          SORT_OPTIONS.POPULAR.code,
          SORT_OPTIONS.NEW.code,
          SORT_OPTIONS.LIKE.code,
          SORT_OPTIONS.DISCOUNT.code,
          SORT_OPTIONS.LOW_PRICE.code,
          SORT_OPTIONS.HIGH_PRICE.code,
        ];
      default:
        return null;
    }
  }, [origin]);

  const sortOptions = useMemo(() => {
    if (customSortOptions) {
      return customSortOptions;
    }

    const all = getSortOptionsArray();
    if (originAllowedSortCodes && originAllowedSortCodes.length) {
      return all.filter((opt) => originAllowedSortCodes.includes(opt.code));
    }
    return all;
  }, [customSortOptions, originAllowedSortCodes]);

  const handleClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, handleClickOutside]);

  // ESC 키로 드롭다운 닫기
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen]);

  // 드롭다운 토글 핸들러
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // 정렬 옵션 선택 핸들러를 메모이제이션
  const handleSortSelect = useCallback(
    (sortCode) => {
      onSortChange(sortCode);
      setIsOpen(false);
    },
    [onSortChange],
  );

  // 현재 선택된 정렬 옵션의 제목을 메모이제이션
  const selectedSortTitle = useMemo(() => {
    const option = sortOptions.find(
      (option) => option.code === selectedSort,
    );
    return option ? option.title : "정렬";
  }, [selectedSort, sortOptions]);

  // If current selectedSort is not allowed under current origin restriction, auto-correct to default
  const defaultSortCode = useMemo(() => {
    if (customSortOptions?.length) {
      return customSortOptions[0].code;
    }

    switch (origin) {
      case "SELLER":
        return SORT_OPTIONS.NEW.code;
      default:
        return SORT_OPTIONS.POPULAR.code;
    }
  }, [customSortOptions, origin]);

  useEffect(() => {
    const allowedSortCodes = customSortOptions?.length
      ? customSortOptions.map((option) => option.code)
      : originAllowedSortCodes;

    if (allowedSortCodes?.length && !allowedSortCodes.includes(selectedSort)) {
      onSortChange(defaultSortCode);
    }
  }, [customSortOptions, originAllowedSortCodes, selectedSort, defaultSortCode, onSortChange]);

  return (
    <div className="SortDropdown" ref={dropdownRef}>
      <button
        className="SortDropdown-button"
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="정렬 옵션 선택"
      >
        {selectedSortTitle}
        <img
          src={IconArrowDownUp}
          alt=""
          aria-hidden="true"
          className={`SortDropdown-icon ${isOpen ? "SortDropdown-icon--open" : ""}`}
        />
      </button>

      {isOpen && (
        <ul className="SortDropdown-menu" role="listbox" aria-label="정렬 옵션">
          {sortOptions.map((option) => (
            <li
              key={option.code}
              className={`SortDropdown-item ${selectedSort === option.code ? "SortDropdown-item--active" : ""}`}
              onClick={() => handleSortSelect(option.code)}
              role="option"
              aria-selected={selectedSort === option.code}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSortSelect(option.code);
                }
              }}
            >
              {option.title}
              {selectedSort === option.code && (
                <span className="SortDropdown-check" aria-hidden="true">
                  ✓
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

SortDropdown.propTypes = {
  selectedSort: PropTypes.string.isRequired,
  onSortChange: PropTypes.func.isRequired,
  origin: PropTypes.string,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ),
};

export default SortDropdown;
